import React, { useCallback, useEffect, useState } from 'react'
import { Text } from 'react-native-paper'
import fb from '../../../utils/service/fb'
import Layout from '../../Auth/Layout'
import { Input } from '../../../ui/atoms/builder-2.0/Input'
import { observer } from 'mobx-react'
import SubmitButton from '../../Auth/SubmitButton'
import Title, { SubTitle } from '../../Auth/Title'
import { Link } from '../../../ui/atoms/Link'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { ErrorCode } from '@linqpal/models/src/types/exceptions'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from '../../Auth/ErrorMessage'
import { Spacer } from '../../../ui/atoms'
import {
  validateEmail,
  validateUSPhoneNumber,
} from '@linqpal/models/src/helpers/validations'
import useRecaptcha from '../../Auth/useRecaptcha'

export type onNextPayload = {
  login: string
  type: 'email' | 'phone'
  verifier: fb.auth.RecaptchaVerifier
}

interface SignInProps {
  onNext: (payload: onNextPayload) => void
  onSignUp: () => void
  login?: string
  loading: boolean
  error?: ErrorCode | false
}

const validateLogin = (login: string) =>
  validateEmail(login) || validateUSPhoneNumber(login)

const SignIn: React.FC<SignInProps> = observer(
  ({ onNext, onSignUp, login: initLogin, error, loading }) => {
    const [login, setLogin] = useState<string>(initLogin || '')
    const { t } = useTranslation('auth')

    const { verifier, createVerifier } = useRecaptcha('verify-phone')

    useEffect(() => {
      createVerifier()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleNext = useCallback(() => {
      try {
        const type = validateEmail(login) ? 'email' : 'phone'
        if (!verifier) throw new Error('RecaptchaVerifier was not created')
        onNext({ login, type, verifier })
      } catch (err) {
        console.error(err)
      }
    }, [login, onNext, verifier])

    const handleChange = useCallback(
      (value: string) => {
        setLogin(value)
      },
      [setLogin],
    )

    const isValidLogin = validateLogin(login)

    return (
      <Layout showsBackButton={false}>
        <Title testID="login-title">{t('signIn.title')}</Title>
        <SubTitle style={{ marginTop: 20 }}>{t('subTitle')}</SubTitle>
        <Spacer height={40} />
        <Input
          testID="login"
          value={login}
          onChangeText={handleChange}
          label={t('signIn.loginLabel')}
          autoFocus
          mandatory
        />
        <ErrorMessage error={error} />
        <Spacer height={32} />
        <SubmitButton
          onPress={handleNext}
          disabled={!isValidLogin || loading}
          loading={loading}
          testID={'sign-in-submit-btn'}
        >
          {t('signIn.submitLabel')}
        </SubmitButton>
        <Spacer height={40} />
        <Text
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 16,
            fontWeight: '500',
            color: commonColors.textLight,
            alignSelf: 'center',
          }}
          testID="link-to-signup"
        >
          {`${t('signIn.signUpQuestion')} `}
          <Link
            textStyle={{ fontSize: 16, fontWeight: '500' }}
            title={t('SignUp')}
            color={commonColors.accentText}
            testID="signup_link"
            onPress={onSignUp}
          />
        </Text>
      </Layout>
    )
  },
)

export default SignIn
